import {useTranslation} from "react-i18next";
import {Button, Grid, Surface, Table, Text, useTheme} from "tmwx-design-web/src";
import {useDispatch, useSelector} from "react-redux";
import {usePermissions} from "tmwx-design-common";
import {useEffect, useMemo} from "react";
import {RootState} from "gui-common/redux/store";
import {ColumnDef} from "@tanstack/react-table";
import {isEmpty} from "gui-common/appHelper";
import {setSnackbarData} from "gui-common/redux/action/snackbarAction";
import {Company} from "gui-common/redux/data/company";
import {getDeviceList} from "gui-common/redux/action/efe/deviceAction";
import {Device} from "gui-common/redux/data/device";

const DeviceList = () => {
    const { t } = useTranslation()
    const { padding, roundness } = useTheme()
    const dispatch: any = useDispatch();
    const { hasRights } = usePermissions()
    const deviceList = useSelector<RootState, Device[]>(state => state.device.deviceList);
    const loading = useSelector<RootState, boolean>(state => state.device.loading);
    const activeCompany = useSelector<RootState, Company>(state => state.auth.activeCompany);

    const columns = useMemo<ColumnDef<Device>[]>(
        () => [
            {
                accessorKey: 'systemId',
                meta: t('efe.id'),
                size: 50,
                header: () => <Text bold>{t('efe.id')}</Text>,
                cell: ({ row }) => (
                    !isEmpty(row.original.systemId) && < Button
                        status='neutral'
                        size='small'
                        uppercase={false}
                        onPress={() => {
                            navigator.clipboard.writeText(row.original.systemId)
                            dispatch(setSnackbarData({
                                status: 'success',
                                text: t('copyToClipboard')
                            }))
                        }}>{row.original.systemId}</Button>
                )
            },
            {
                accessorKey: 'firmwareVersion',
                meta: t('atr.firmware.versionName'),
                header: () => <Text bold>{t('atr.firmware.versionName')}</Text>,
                cell: ({ row }) => {
                    const version = row.original.firmwareVersion;
                    return <Text>{version === -1 ? t('efe.notAdded') : version}</Text>;
                }
            },
            {
                accessorKey: 'registrationDate',
                meta: t('efe.registrationDate'),
                minSize: 70,
                header: () => <Text bold>{t('efe.registrationDate')}</Text>,
            },
            {
                accessorKey: 'lastCommunicationDate',
                meta: t('atr.machines.lastCommunicationDate'),
                minSize: 70,
                header: () => <Text bold>{t('atr.machines.lastCommunicationDate')}</Text>,
            },
            {
                accessorKey: 'lastResetDate',
                meta: t('efe.lastResetDate'),
                minSize: 70,
                header: () => <Text bold>{t('efe.lastResetDate')}</Text>,
            }
        ],
        [dispatch, hasRights, padding, t]
    );

    useEffect(() => {
        if (hasRights(["efe_efe_list"]) && activeCompany.id !== undefined ) {
            dispatch(getDeviceList(activeCompany.id));
        }
    }, [dispatch, activeCompany])

    return (
        <Surface style={{ height: '100vh', paddingTop: 8, paddingBottom: 8 }}>
            <Grid.Container style={{ borderRadius: roundness }} maxWidth='xl' elevation={0} centered>
                <Grid.Col>
                    <Table
                        id="DEVICE_TABLE"
                        columns={columns}
                        data={deviceList}
                        loading={loading}
                        refreshData={() => dispatch(getDeviceList(activeCompany.id))}
                        dense
                        useGlobalFilter
                        useColumnVisibility
                        labels={{
                            add: t('add'),
                            deletePreferences: t('deletePreferences'),
                            jumpHere: t('jumpHere'),
                            page: t('page'),
                            refresh: t('refresh'),
                            row: t('row'),
                            rowsSelected: t('rowsSelected'),
                            savePreferencies: t('savePreferences'),
                            searchData: t('searchData'),
                            selectCurrentPageData: t('selectCurrentPageData'),
                            showAll: t('showAll'),
                            sizing: t('sizing')
                        }}
                    />
                </Grid.Col>
            </Grid.Container>
        </Surface>
    )
}

export default DeviceList;